'use strict';

const moment = require('moment');
const { isNil } = require('lodash');

const DEFAULT_MAX_DAYS_OUTSIDE_SCENARIO = 90;

const promotionDateRangeErrors = Object.freeze({
    outsideScenario: 'outsideScenario',
    noScenarioOverlap: 'noScenarioOverlap',
    tooFarFromScenario: 'tooFarFromScenario',
});

/**
 * Normalises a date to the start of its UTC day so comparisons are day-granular.
 *
 * @param {Date|String} date - The date to normalise.
 * @returns {moment.Moment}
 */
function toUtcDay(date) {
    return moment.utc(date).startOf('day');
}

/**
 * Resolves the number of days a promotion may extend beyond its scenario.
 *
 * @param {Number} maxDaysOutsideScenario - The configured value.
 * @returns {Number}
 */
function resolveMaxDaysOutsideScenario(maxDaysOutsideScenario) {
    const days = Number(maxDaysOutsideScenario);
    return Number.isFinite(days) && days >= 0 ? days : DEFAULT_MAX_DAYS_OUTSIDE_SCENARIO;
}

/**
 * Gets the earliest start and latest end date a promotion may have within a scenario.
 *
 * @param {Object} params
 * @param {Object} params.scenario - The scenario with startDate and endDate.
 * @param {Boolean} params.allowOutsideScenario - Whether promotions may extend beyond the scenario.
 * @param {Number} params.maxDaysOutsideScenario - Max number of days a promotion may extend beyond the scenario.
 * @returns {{ minDate: moment.Moment, maxDate: moment.Moment }}
 */
function getPromotionDateBounds({ scenario, allowOutsideScenario, maxDaysOutsideScenario }) {
    const minDate = toUtcDay(scenario.startDate);
    const maxDate = toUtcDay(scenario.endDate);

    if (!allowOutsideScenario) {
        return { minDate, maxDate };
    }

    const days = resolveMaxDaysOutsideScenario(maxDaysOutsideScenario);
    return {
        minDate: minDate.subtract(days, 'days'),
        maxDate: maxDate.add(days, 'days'),
    };
}

/**
 * Validates promotion dates against its scenario dates.
 * When promotions may extend beyond the scenario, they must overlap it by at least one day
 * and stay within the configured number of days from it. Otherwise they must lie within it.
 *
 * @param {Object} params
 * @param {Object} params.promotion - The promotion with startDate and endDate.
 * @param {Object} params.scenario - The scenario with startDate and endDate.
 * @param {Boolean} params.allowOutsideScenario - Whether promotions may extend beyond the scenario.
 * @param {Number} params.maxDaysOutsideScenario - Max number of days a promotion may extend beyond the scenario.
 * @returns {String|null} - A promotionDateRangeErrors value or null when valid.
 */
function getPromotionDateRangeError({
    promotion,
    scenario,
    allowOutsideScenario = false,
    maxDaysOutsideScenario,
}) {
    if (
        isNil(promotion) ||
        isNil(scenario) ||
        isNil(promotion.startDate) ||
        isNil(promotion.endDate) ||
        isNil(scenario.startDate) ||
        isNil(scenario.endDate)
    ) {
        return null;
    }

    const promotionStart = toUtcDay(promotion.startDate);
    const promotionEnd = toUtcDay(promotion.endDate);
    const scenarioStart = toUtcDay(scenario.startDate);
    const scenarioEnd = toUtcDay(scenario.endDate);

    if (!allowOutsideScenario) {
        return promotionStart.isBefore(scenarioStart) || promotionEnd.isAfter(scenarioEnd)
            ? promotionDateRangeErrors.outsideScenario
            : null;
    }

    if (promotionStart.isAfter(scenarioEnd) || promotionEnd.isBefore(scenarioStart)) {
        return promotionDateRangeErrors.noScenarioOverlap;
    }

    const { minDate, maxDate } = getPromotionDateBounds({
        scenario,
        allowOutsideScenario,
        maxDaysOutsideScenario,
    });
    if (promotionStart.isBefore(minDate) || promotionEnd.isAfter(maxDate)) {
        return promotionDateRangeErrors.tooFarFromScenario;
    }

    return null;
}

/**
 * Checks whether promotion dates are valid for its scenario.
 *
 * @param {Object} params - See getPromotionDateRangeError.
 * @returns {Boolean}
 */
function isPromotionDateRangeValid(params) {
    return isNil(getPromotionDateRangeError(params));
}

module.exports = {
    DEFAULT_MAX_DAYS_OUTSIDE_SCENARIO,
    promotionDateRangeErrors,
    resolveMaxDaysOutsideScenario,
    getPromotionDateBounds,
    getPromotionDateRangeError,
    isPromotionDateRangeValid,
};
